import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import AdminLayout from '../../components/AdminLayout';

const emptyForm = { title:'', slug:'', excerpt:'', content:'', cover_image:'', author:'StyleMe Team', tags:'', status:'draft' };

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9\s-]/g,'').trim().replace(/\s+/g,'-');

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [view, setView] = useState('list'); // 'list' | 'edit'
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [igPosting, setIgPosting] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);

  const load = () => api.get('/blog/admin/all').then(r => setPosts(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openNew = () => { setForm(emptyForm); setEditId(null); setView('edit'); setPreview(false); };
  const openEdit = async (id) => {
    const r = await api.get(`/blog/admin/${id}`);
    setForm({ title:r.data.title, slug:r.data.slug, excerpt:r.data.excerpt||'', content:r.data.content||'',
      cover_image:r.data.cover_image||'', author:r.data.author||'StyleMe Team', tags:r.data.tags||'', status:r.data.status });
    setEditId(id); setView('edit'); setPreview(false);
  };

  const handleSave = async (status) => {
    setSaving(true);
    try {
      const data = { ...form, status };
      if (editId) await api.put(`/blog/${editId}`, data);
      else await api.post('/blog', data);
      await load(); setView('list');
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    await api.delete(`/blog/${id}`); load();
  };

  const postToInstagram = async (id) => {
    setIgPosting(true);
    try {
      const r = await api.post(`/blog/${id}/post-instagram`);
      if (r.data.error) alert('Instagram error: ' + r.data.error);
      else if (r.data.skipped) alert('Skipped: ' + (r.data.reason || 'Instagram not configured'));
      else alert('✅ Posted to Instagram successfully!');
    } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
    finally { setIgPosting(false); }
  };

  const markdownToHtml = (md) => {
    if (!md) return '';
    return md
      .replace(/^### (.+)$/gm, '<h3 style="font-size:19px;font-weight:700;margin:24px 0 10px;color:#374151">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 style="font-size:22px;font-weight:800;margin:28px 0 12px;color:#111;border-left:4px solid #4f46e5;padding-left:14px">$1</h2>')
      .replace(/^# (.+)$/gm, '<h1 style="font-size:32px;font-weight:900;margin:32px 0 14px;color:#111">$1</h1>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/^==(.+)==$/gm, '<div style="background:linear-gradient(135deg,#f0f0ff,#e8e8ff);border-left:4px solid #4f46e5;border-radius:0 12px 12px 0;padding:16px 20px;margin:20px 0;font-style:italic;color:#374151">$1</div>')
      .replace(/^>>(.+)<<$/gm, '<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:12px 16px;margin:16px 0;color:#92400e">💡 $1</div>')
      .replace(/!\[(.+?)\]\((.+?)\)/g, '<figure style="margin:24px 0;text-align:center"><img src="$2" alt="$1" style="max-width:100%;border-radius:14px;box-shadow:0 4px 16px rgba(0,0,0,0.1)"/><figcaption style="font-size:13px;color:#9ca3af;margin-top:6px;font-style:italic">$1</figcaption></figure>')
      .replace(/\[SHOP:\s*(.+?)\]\((.+?)\)/g, '</p><div style="margin:12px 0"><a href="$2" style="display:inline-flex;align-items:center;gap:8px;background:linear-gradient(135deg,#4f46e5,#7c3aed);color:white;padding:10px 22px;border-radius:100px;font-weight:700;font-size:14px;text-decoration:none;box-shadow:0 4px 12px rgba(79,70,229,0.3)">🛒 $1 →</a></div><p style="font-size:16px;line-height:1.8;color:#374151;margin:0 0 14px">')
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" style="color:#4f46e5;text-decoration:underline">$1</a>')
      .replace(/^---$/gm, '<hr style="border:none;border-top:2px solid #f3f4f6;margin:32px 0"/>')
      .replace(/^- (.+)$/gm, '<li style="padding:6px 0 6px 24px;position:relative;font-size:16px;color:#374151;list-style:none">• $1</li>')
      .split('\n').map(l => l.trim().startsWith('<') ? l : (l.trim() ? `<p style="font-size:16px;line-height:1.8;color:#374151;margin:0 0 14px">${l}</p>` : '')).join('\n');
  };

  const ta = { width:'100%', padding:'10px 14px', border:'1.5px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box', fontFamily:'inherit' };

  if (view === 'edit') return (
    <AdminLayout title="Blog Editor">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <button className="btn btn-outline btn-sm" onClick={() => setView('list')}>&larr; Back to Posts</button>
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn btn-outline btn-sm" onClick={() => setPreview(!preview)}>{preview ? 'Edit' : 'Preview'}</button>
          {editId && <button onClick={() => postToInstagram(editId)} disabled={igPosting} style={{ padding:'8px 16px', background:'linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)', color:'white', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>{igPosting ? 'Posting...' : '📸 Post to Instagram'}</button>}
          <button className="btn btn-outline" onClick={() => handleSave('draft')} disabled={saving}>Save Draft</button>
          <button className="btn btn-primary" onClick={() => handleSave('published')} disabled={saving}>
            {saving ? 'Saving...' : editId ? 'Update & Publish' : 'Publish Post'}
          </button>
        </div>
      </div>

      {preview ? (
        <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:12, padding:'40px', maxWidth:800, margin:'0 auto' }}>
          {form.cover_image && <img src={form.cover_image} alt="" style={{ width:'100%', borderRadius:12, marginBottom:24, maxHeight:400, objectFit:'cover' }}/>}
          {form.tags && (
            <div style={{ marginBottom:14 }}>
              {form.tags.split(',').map(t => t.trim()).filter(Boolean).map(t => (
                <span key={t} style={{ fontSize:11, background:'#ede9fe', color:'#4f46e5', borderRadius:100, padding:'4px 12px', marginRight:8, fontWeight:600 }}>{t}</span>
              ))}
            </div>
          )}
          <h1 style={{ fontSize:36, fontWeight:900, marginBottom:12, lineHeight:1.2 }}>{form.title || 'Untitled Post'}</h1>
          {form.excerpt && <p style={{ fontSize:18, color:'#6b7280', marginBottom:24 }}>{form.excerpt}</p>}
          <div style={{ fontSize:15, lineHeight:1.8, color:'#374151' }} dangerouslySetInnerHTML={{ __html: markdownToHtml(form.content) }} />
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div>
              <label style={{ fontSize:13, fontWeight:700, display:'block', marginBottom:5 }}>Title *</label>
              <input style={ta} value={form.title} onChange={e => { set('title', e.target.value); if (!editId) set('slug', slugify(e.target.value)); }} placeholder="Your blog post title" />
            </div>
            <div>
              <label style={{ fontSize:13, fontWeight:700, display:'block', marginBottom:5 }}>URL Slug</label>
              <input style={{ ...ta, fontFamily:'monospace', color:'#4f46e5' }} value={form.slug} onChange={e => set('slug', slugify(e.target.value))} placeholder="url-friendly-slug" />
              {form.slug && <div style={{ fontSize:12, color:'#9ca3af', marginTop:4 }}>styleme.club/blog/{form.slug}</div>}
            </div>
            <div>
              <label style={{ fontSize:13, fontWeight:700, display:'block', marginBottom:5 }}>Excerpt (shown in blog list)</label>
              <textarea style={{ ...ta, minHeight:70, resize:'vertical' }} value={form.excerpt} onChange={e => set('excerpt', e.target.value)} placeholder="Brief description of the post..." />
            </div>
            <div>
              <label style={{ fontSize:13, fontWeight:700, display:'block', marginBottom:5 }}>Cover Image URL (optional)</label>
              <input style={ta} value={form.cover_image} onChange={e => set('cover_image', e.target.value)} placeholder="https://..." />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={{ fontSize:13, fontWeight:700, display:'block', marginBottom:5 }}>Author</label>
                <input style={ta} value={form.author} onChange={e => set('author', e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize:13, fontWeight:700, display:'block', marginBottom:5 }}>Tags (comma separated)</label>
                <input style={ta} value={form.tags} onChange={e => set('tags', e.target.value)} placeholder="jewelry, fashion, style" />
              </div>
            </div>
          </div>
          <div>
            <label style={{ fontSize:13, fontWeight:700, display:'block', marginBottom:5 }}>
              Content (Markdown)
              <span style={{ fontSize:11, color:'#9ca3af', fontWeight:400, marginLeft:8 }}>
                # H1 &nbsp; ## H2 &nbsp; **bold** &nbsp; *italic* &nbsp; ![alt](url)
              </span>
            </label>
            <textarea style={{ ...ta, minHeight:520, resize:'vertical', fontFamily:'monospace', fontSize:13, lineHeight:1.6 }}
              value={form.content} onChange={e => set('content', e.target.value)}
              placeholder={`# Your Post Title\n\nWrite your content here using markdown.\n\n## Section Heading\n\nParagraph text goes here. You can use **bold** and *italic* text.\n\n![Image description](https://image-url.com/image.jpg)`}
            />
          </div>
        </div>
      )}
    </AdminLayout>
  );

  return (
    <AdminLayout title="Blog">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h2 style={{ margin:0, fontSize:20, fontWeight:800 }}>Blog Posts</h2>
          <p style={{ color:'#6b7280', fontSize:14, margin:'4px 0 0' }}>{posts.filter(p=>p.status==='published').length} published, {posts.filter(p=>p.status==='draft').length} drafts</p>
        </div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          <a href="/blog" target="_blank" className="btn btn-outline btn-sm">View Blog &rarr;</a>
          <button
            onClick={async () => {
              setAiGenerating(true);
              try {
                const r = await api.post('/blog/generate');
                alert(`✅ AI post created: "${r.data.title}"\n\nIt's now published and posted to Instagram!`);
                load();
              } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
              finally { setAiGenerating(false); }
            }}
            disabled={aiGenerating}
            style={{ padding:'8px 16px', background:'linear-gradient(135deg,#10b981,#059669)', color:'white', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer', opacity: aiGenerating ? 0.7 : 1 }}
          >
            {aiGenerating ? '⏳ Generating...' : '✨ AI Generate Post'}
          </button>
          <button className="btn btn-primary" onClick={openNew}>+ New Post</button>
        </div>
      </div>

      {posts.length === 0 ? (
        <div className="empty-state">
          <div className="icon">&#128221;</div>
          <h3>No blog posts yet</h3>
          <p>Create your first post to start attracting Google traffic.</p>
          <button className="btn btn-primary" style={{ marginTop:16 }} onClick={openNew}>Write Your First Post</button>
        </div>
      ) : (
        <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:12, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead style={{ background:'#f9fafb' }}>
              <tr>
                {['Title','Status','Published','Tags','Actions'].map(h => (
                  <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#6b7280', textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {posts.map(p => (
                <tr key={p.id} style={{ borderTop:'1px solid #f0f0f0' }}>
                  <td style={{ padding:'12px 16px' }}>
                    <div style={{ fontWeight:600 }}>{p.title}</div>
                    <div style={{ fontSize:12, color:'#9ca3af', fontFamily:'monospace' }}>/{p.slug}</div>
                  </td>
                  <td style={{ padding:'12px 16px' }}>
                    <span style={{ fontSize:12, borderRadius:4, padding:'3px 10px', fontWeight:600, background:p.status==='published'?'#dcfce7':'#fef3c7', color:p.status==='published'?'#166534':'#92400e' }}>
                      {p.status}
                    </span>
                  </td>
                  <td style={{ padding:'12px 16px', fontSize:13, color:'#6b7280' }}>{p.published_at ? p.published_at.slice(0,10) : '—'}</td>
                  <td style={{ padding:'12px 16px', fontSize:12, color:'#6b7280' }}>{p.tags || '—'}</td>
                  <td style={{ padding:'12px 16px' }}>
                    <div style={{ display:'flex', gap:6 }}>
                      <button className="btn btn-sm btn-outline" onClick={() => openEdit(p.id)}>Edit</button>
                      {p.status==='published' && <a href={`/blog/${p.slug}`} target="_blank" className="btn btn-sm btn-outline">View</a>}
                      <button className="btn btn-sm btn-danger" onClick={() => del(p.id, p.title)}>Del</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
